import { UIElement } from '../ui-element.js';
import { Labels } from '../../global/labels.js';
import * as UIDropdownHelpers from '../dropdown/ui-dropdown.helpers.js';
import * as UISliderHelpers from '../slider/ui-slider.helpers.js';
import * as UIDatePickerHelpers from '../datetime/ui-datepicker.helpers.js';
import * as UIAutocompleteHelpers from '../autocomplete/ui-autocomplete.helpers.js';
import { UIHint } from '../hint/ui-hint.js';
import {
    getData,
    setData,
    mergeAttributes,
    BaseNativeFormElements,
    NativeFormElements,
    CustomFormElements,
    CustomVirtualFormElements,
    element,
    keepAllowedElements,
    createElement,
} from '../../global/ui-helpers.js';
import { Digest } from '../../global/digest.js';
import {
    updateAriaLabels,
    handleLabelClick,
} from './ui-field-accessibility.js';
import { browser } from '../../global/browser.js';
import { debounce } from '../../global/helpers.js';
import styles from './ui-field.css';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIField
 * @element ui-field
 * @classdesc Represents a class for <code>ui-field</code> element.
 * Container for input elements, ui-field is very useful element that
 * handles errors, gives more opportunities to input elements. If you are
 * working with form, it is strongly recommended to wrap all input with
 * ui-field.
 * @property {boolean} [required] {@attr required} If true, will be set
 * a red '*' symbol to the label.
 * @property {string} [label] {@attr label} Label of the field.
 * @property {string} [units] {@attr units} Text for units.
 * @property {UIFieldLayouts} [layout] {@attr layout} Layout of field.
 *  {@desc fluid: Full width container.}
 *  {@desc inline: Field and label are aligned to left with auto width.}
 *  {@desc equal: Label and field are aligned to left and have 50% width.}
 * @property {boolean} [actionAdd] {@attr action-add} Adds button with only icon plus. Button has data-event=ui-field-add.
 * @property {boolean} [actionRemove] {@attr action-remove} Adds button with only icon cross. Button has data-event=ui-field-remove.
 * @property {HTMLDivElement} control {@readonly} Shortcut for control wrapper.
 * Wrapper can contain many inputs.
 * @property {HTMLDivElement} labelContainer {@readonly} Shortcut for label wrapper.
 * Wrapper can contain labels and tooltips.
 * @property {HTMLElement} trailingAction {@readonly} Shortcut for trailing action wrapper.
 * Contains add/remove or custom trailing action button.
 * @property {HTMLElement} inputGroup {@readonly} Shortcut for ui-inputgroup wrapper.
 * Contains controls and trailing actions, except hints.
 * @property {HTMLElement} unitsElement {@readonly} Shortcut for units element.
 * @slot
 * @example
 * <ui-field required label="Your name">
 *   <input type="text" name="your-name">
 * </ui-field>
 */
class UIField extends UIElement {
    /**
     * Layout type of field.
     * @type {UIFieldLayouts}
     * @readonly
     */
    static get Layout() {
        return {
            Fluid: 'fluid',
            Inline: 'inline',
            Equal: 'equal',
        };
    }
    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels() {
        return Labels.attach('ui-field', {
            required: 'Required',
            add: 'Add',
            remove: 'Remove',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes() {
        return ['label', 'required', 'action-add', 'action-remove', 'units'];
    }

    /**
     * @type {string[]}
     * @readonly
     */
    static get allowedTrailingElementTags() {
        return ['a', 'ui-link', 'button', 'ui-button'];
    }

    /**
     * @type {Props}
     * @readonly
     */
    /** TODO: fix trailingAction selector after GUI-3394 */
    static get props() {
        return {
            attributes: {
                required: Boolean,
                label: String,
                layout: String,
                actionAdd: Boolean,
                actionRemove: Boolean,
                units: String,
            },
            children: {
                labelContainer: '.ui-field__label',
                control: '.ui-field__control',
                trailingAction: '.ui-field__trailing-action',
                inputGroup: '.ui-field__inputgroup',
                unitsElement: '.ui-field__units',
            },
        };
    }

    /**
     * Convert data config to element config
     * @param {FieldConfig} config
     * @returns {ElementConfig}
     */
    static fromFieldConfig(config) {
        let controls = [];
        if (config.control) {
            const ctrl = UIField.buildControlFromConfig(config.control);
            if (Array.isArray(ctrl)) {
                controls = controls.concat(ctrl);
            } else {
                controls.push(ctrl);
            }
        }

        if (config.controls) {
            Object.keys(config.controls).forEach((key) => {
                const ctrl = UIField.buildControlFromConfig(
                    config.controls[key]
                );
                if (Array.isArray(ctrl)) {
                    controls = controls.concat(ctrl);
                } else {
                    controls.push(ctrl);
                }
            });
        }

        if (Array.isArray(config.hints) && config.hints.length > 0) {
            config.hints.forEach((hint) => {
                controls.push({
                    tagName: hint.type !== 'span' ? 'ui-hint' : 'span',
                    attributes: hint.type && {
                        'type': hint.type,
                        'error-type': hint.errorType || null,
                    },
                    children: [hint.content],
                });
            });
        }

        if (config.tooltip) {
            controls.push({
                tagName: 'ui-tooltip',
                attributes: {
                    type: 'question',
                    slot: 'label',
                },
                children: [config.tooltip.content],
            });
        }

        const isControlRequired = (ctrl) => {
            if (ctrl.attributes && ctrl.attributes.required) {
                return true;
            }
            if (Array.isArray(ctrl.children) && ctrl.children.length) {
                return ctrl.children.some(isControlRequired);
            }
            return false;
        };

        return {
            tagName: 'ui-field',
            attributes: {
                'key': 'id' in config ? config.id : null,
                'label': config.label,
                'data-index': 'index' in config ? config.index : null,
                'aria-hidden':
                    'visible' in config && !config.visible ? 'true' : null,
                'required': controls.some(isControlRequired) || null,
            },
            classList: {
                '-hidden': 'visible' in config ? !config.visible : false,
                '-error': 'invalid' in config ? config.invalid : false,
            },
            children: controls,
        };
    }

    /**
     * Create an ElementConfig based on FieldControl.
     * @param {FieldControl} control
     * @returns {ElementConfig | Array<ElementConfig>}
     */
    static buildControlFromConfig(control) {
        const inputControls = [];
        switch (control.type) {
            case 'input':
                return {
                    tagName: 'input',
                    attributes: mergeAttributes(
                        {
                            type: 'text',
                            value: 'value' in control ? control.value : null,
                        },
                        control.attributes
                    ),
                    classList: control.classList || {},
                };
            case 'textarea':
                return {
                    tagName: 'textarea',
                    attributes: control.attributes,
                };
            case 'select':
                return {
                    tagName: 'select',
                    attributes: control.attributes,
                    classList: control.classList || {},
                    children:
                        'options' in control &&
                        control.options.map((option) => {
                            return {
                                tagName: 'option',
                                attributes: {
                                    value: option.value,
                                    selected:
                                        option.value === control.value
                                            ? ''
                                            : null,
                                },
                                children: [option.content],
                            };
                        }),
                };

            case 'radio':
                return {
                    tagName: 'ul',
                    classList: {
                        'input-list': true,
                    },
                    children:
                        'options' in control &&
                        control.options.map((option) => {
                            return {
                                tagName: 'li',
                                children: [
                                    control.options.length > 0 && {
                                        tagName: 'input',
                                        classList: option.classList || null,
                                        attributes: mergeAttributes(
                                            {
                                                type: 'radio',
                                                name: option.name,
                                                value: option.value,
                                                id: option.value,
                                                checked:
                                                    option.value ===
                                                    control.value
                                                        ? ''
                                                        : null,
                                            },
                                            option.attributes
                                        ),
                                    },
                                    {
                                        tagName: 'label',
                                        attributes: {
                                            for: option.value,
                                        },
                                        children: [option.content],
                                    },
                                ],
                            };
                        }),
                };

            case 'radio-horizontal':
                return (
                    'options' in control &&
                    control.options.reduce((elements, option) => {
                        return elements.concat([
                            control.options.length > 0 && {
                                tagName: 'input',
                                classList: option.classList || null,
                                attributes: mergeAttributes(
                                    {
                                        type: 'radio',
                                        name: option.name,
                                        value: option.value,
                                        id: option.id,
                                        checked:
                                            option.value === control.value
                                                ? ''
                                                : null,
                                    },
                                    option.attributes
                                ),
                            },
                            {
                                tagName: 'label',
                                attributes: {
                                    for: option.id,
                                },
                                children: [option.content],
                            },
                        ]);
                    }, [])
                );
            case 'inputgroup':
                if (control.control) {
                    inputControls.push(control.control);
                }
                if (control.controls) {
                    Object.keys(control.controls).forEach((key) => {
                        inputControls.push(control.controls[key]);
                    });
                }
                return {
                    tagName: 'ui-inputgroup',
                    attributes: control.attributes,
                    children: inputControls.map(UIField.buildControlFromConfig),
                };
            case 'autocomplete':
                return UIAutocompleteHelpers.fromControlConfig(control);
            case 'dropdown':
                return UIDropdownHelpers.fromControlConfig(control);
            case 'slider':
                return UISliderHelpers.fromControlConfig(control);
            case 'date':
                return UIDatePickerHelpers.fromControlConfig(control);

            default:
                return null;
        }
    }

    /**
     * Set object data to nested controls, where the name attribute is a key property.
     * @param {Record<string, string>} data Data to be applied to the node.
     * @returns {UIField}
     */
    setData(data) {
        return setData(this, data);
    }

    /**
     * Get object data from nested controls, where the name attribute is a key property.
     * @returns {Record<string, string>}
     */
    getData() {
        return getData(this);
    }

    /**
     * Adds validation error under the field.
     * @param {string} message
     * @param {string} errorType
     * @returns {UIField}
     */
    addError(message, errorType) {
        this.addHint({
            type: UIHint.types.ERROR,
            content: message,
            errorType: errorType,
        });
        return this;
    }

    /**
     * Adds validation error under the field.
     * @param {string} message
     * @param {string} errorType
     * @returns {UIField}
     */
    setError(message, errorType) {
        const prevError = this.querySelector(
            'ui-hint[error-type=' + errorType + ']'
        );
        if (prevError) {
            prevError.setMessage(message);
        } else {
            this.addError(message, errorType);
        }
        return this;
    }

    /**
     * Remove all hints from ui-field. All hints will be removed
     * doesn't matter what type they have.
     * @returns {UIField}
     */
    removeHints() {
        const hints = this.querySelectorAll('ui-hint');
        [].forEach.call(hints, (hint) => {
            hint.parentElement.removeChild(hint);
        });
        this.checkErrors();
        return this;
    }

    /**
     * Adds hint under the field.
     * @param {FieldHint} hint
     * @returns {UIField}
     */
    addHint(hint) {
        const children = [
            {
                tagName: 'label',
                attributes: {
                    for: this.getControlId(),
                    role: hint.type === UIHint.types.ERROR ? 'alert' : null,
                },
                children: [hint.content],
            },
        ];

        /** @type {Element} */
        const hintElement = this.createElement({
            tagName: 'ui-hint',
            attributes: {
                'type': hint.type,
                'error-type': hint.errorType || null,
            },
            children,
        });

        // If this is an error hint, insert it before any existing non-error hints
        if (hint.type === UIHint.types.ERROR) {
            // Handle ui-textcounter case.
            /** @type {UITextCounter} */
            const textCounter = this.control.querySelector('ui-textcounter');
            if (textCounter) {
                textCounter.textarea.insertAdjacentElement(
                    'afterend',
                    hintElement
                );
                return this;
            }
            const firstNonErrorHint = this.control.querySelector(
                `:scope > ui-hint:not([type="${UIHint.types.ERROR}"])`
            );
            if (firstNonErrorHint) {
                this.control.insertBefore(hintElement, firstNonErrorHint);
            } else {
                this.control.appendChild(hintElement);
            }
        } else {
            this.control.appendChild(hintElement);
        }
        this.checkErrors();
        return this;
    }

    /**
     * Creates an add button trailing element
     * @fires event:ui-field-add
     * @private
     * @returns {HTMLElement}
     */
    createAddButton() {
        return element`
            <ui-button
                class="ui-field__trailing-action -add"
                aria-label="${UIField.labels.add}"
                glyph="redesign-circle-plus"
                data-event="ui-field-add"
                layout="tertiary"
            ></ui-button>
        `;
    }

    /**
     * Creates a remove button trailing element
     * @fires event:ui-field-remove
     * @private
     * @returns {HTMLElement}
     */
    createRemoveButton() {
        return element`
            <ui-button
                class="ui-field__trailing-action -remove"
                aria-label="${UIField.labels.remove}"
                glyph="redesign-circle-x-mark"
                data-event="ui-field-remove"
                layout="tertiary"
            ></ui-button>
        `;
    }

    /**
     * Check if a control element is visible (ignores class=-hidden, type, and hidden attribute).
     * @param {HTMLElement} el The element to test
     * @returns {boolean} True if element is not hidden by class, type, or attribute
     */
    isVisibleControl(el) {
        return (
            !el.classList.contains('-hidden') &&
            el.getAttribute('type') !== 'hidden' &&
            !el.hasAttribute('hidden')
        );
    }

    /**
     * Updates label content. Also
     * adds / removes an asterisk to indicate if field is required.
     * @returns {UIField}
     */
    updateLabel() {
        this.setLabelContent(
            [
                this.label || '',
                this.required
                    ? `<span
                        class="ui-field__required-marker"
                        aria-hidden="true"
                    > (${UIField.labels.required})</span>`
                    : '',
            ].join('')
        );
        this.updateAriaLabels();
        return this;
    }

    /**
     * Ensures the trailing action is placed inside <ui-inputgroup>.
     * Moves non-hint children into the input group and appends the trailing action.
     * @param {HTMLElement | null} trailingAction
     */
    applyTrailingActionToControl(trailingAction) {
        if (this.trailingAction) {
            this.trailingAction.remove();
        }

        if (!trailingAction) {
            return;
        }

        let inputGroup = this.inputGroup;
        if (!inputGroup) {
            inputGroup = createElement({
                tagName: 'ui-inputgroup',
                classList: {
                    'ui-field__inputgroup': true,
                },
            });

            const children = Array.from(this.control.childNodes);
            children.forEach((node) => {
                if (
                    !(node instanceof Element) ||
                    node.tagName.toLowerCase() !== 'ui-hint'
                ) {
                    inputGroup.appendChild(node);
                }
            });

            const firstHint = this.control.querySelector(
                ':scope > ui-hint:first-of-type'
            );
            if (firstHint) {
                this.control.insertBefore(inputGroup, firstHint);
            } else {
                this.control.appendChild(inputGroup);
            }
        }
        if (!this.inputGroup.contains(trailingAction)) {
            this.inputGroup.appendChild(trailingAction);
        }
    }

    /**
     * Updates the trailing action button in the control wrapper.
     */
    updateTrailingAction() {
        const trailingAction = this.getTrailingAction();
        this.applyTrailingActionToControl(trailingAction);
    }

    /**
     * Updates aria-labels for date/time components to include the field label context
     * @private
     */
    updateAriaLabels() {
        const control = this.getControl();
        if (!control || !this.label) {
            return;
        }

        updateAriaLabels(control, this.label);
    }

    /**
     * Check if field has error hints inside.
     * @returns {UIField}
     */
    checkErrors() {
        const hasError =
            this.querySelectorAll(
                `ui-hint[type=${UIHint.types.ERROR}]:not(.-hidden)`
            ).length > 0;
        this.updateClassList({ '-error': hasError });
        const control = this.querySelector('input,textarea,select');
        if (!control) {
            return this;
        }
        if (hasError) {
            control.setAttribute('aria-invalid', 'true');
        } else {
            control.removeAttribute('aria-invalid');
        }

        return this;
    }

    /**
     * Gets first validation error for field.
     * @returns {Element | null}
     */
    getError() {
        return (
            this.querySelector(`ui-hint[type=${UIHint.types.ERROR}] label`) ||
            this.querySelector(`ui-hint[type=${UIHint.types.ERROR}]`)
        );
    }

    /**
     * Gets all validation errors for field.
     * @returns {NodeList}
     */
    getAllErrors() {
        return this.querySelectorAll(`ui-hint[type=${UIHint.types.ERROR}]`);
    }

    /**
     * Remove all validation errors. Errors are hints and only hints with type="errors"
     * will be removed.
     * @param {boolean} [force] Remove errors with force and ignores all other conditions.
     * @returns {UIField}
     */
    removeErrors(force = false) {
        this.querySelectorAll(
            `.ui-field__control ui-hint[type=${UIHint.types.ERROR}]`
        ).forEach((/** @type {UIHint} */ node) => {
            const elem = node.parentElement.querySelector(
                'input,textarea,select'
            );

            if (
                force ||
                (elem.validity.valid && node.errorType) ||
                (elem.dataset.errorType &&
                    elem.dataset.errorType === node.errorType)
            ) {
                this.updateClassList({ '-error': false });
                elem.removeAttribute('aria-invalid');
                node.parentElement.removeChild(node);
            }
        });
        return this;
    }

    /**
     * Reads assistive error for a11y.
     */
    announceError() {
        const err = this.getError();
        if (err) {
            err.setAttribute('aria-live', 'polite');
        }
    }

    /**
     * Get id of the field control/input.
     * @returns {string | null}
     */
    getControlId() {
        const control = this.getControl();
        if (!control) {
            return null;
        }

        const nativeBaseNames = NativeFormElements.map(
            (selector) => selector.split(':')[0]
        );

        // Slotted native form element is simply restyled, and no custom element is used. A custom ID attribute can be set on the element itself.
        // Examples: input, textarea
        // TODO GUI-3649: For some reason this also triggers for ui-rating? Investigate why.
        if (nativeBaseNames.includes(control.localName)) {
            let id = control.getAttribute('id');
            if (!id) {
                id = Digest.randomId();
                control.setAttribute('id', id);
            }
            return id;
        }

        // The component belongs to CustomVirtualFormElements. Generally this means the slotted native form element is hidden or there are multiple slotted form elements. Mandates a control-id prop on the custom element host in order to provide a ID with a custom name.
        // Examples: ui-monthpicker, range components
        // TODO GUI-3649: Only complex components which can't have a single native form element in the slot (explaining the need for a common control-id prop) should be here. We should refactor this in future to make more sense.
        if (CustomVirtualFormElements.includes(control.localName)) {
            let id = control.getAttribute('control-id');
            if (!id) {
                id = Digest.randomId();
                control.setAttribute('control-id', id);
            }
            return id;
        }

        // A single native form element is slotted in the custom element.
        // Examples: ui-searchinput, ui-timepicker, ui-autocomplete, ui-upload
        const subControl = control.querySelector(NativeFormElements.join(','));
        if (subControl) {
            const id = subControl.getAttribute('id');
            if (id) {
                control.setAttribute('control-id', id);
                return id;
            }
        }

        let id = control.getAttribute('control-id');
        if (!id) {
            id = Digest.randomId();
            control.setAttribute('control-id', id);
        }

        return id;
    }

    /**
     * Gets the control.
     * @param {boolean} [includeFieldsetFriendly] Include fieldset-friendly HTML elements (radios or checkboxes)
     * @returns {UIElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement}
     */
    getControl(includeFieldsetFriendly = false) {
        const selectors = (
            includeFieldsetFriendly
                ? BaseNativeFormElements
                : NativeFormElements
        )
            .concat(CustomFormElements)
            .join(',');
        return this.querySelector(selectors);
    }

    /**
     * Get trailing action.
     * @returns {HTMLElement | null}
     */
    getTrailingAction() {
        let trailingAction = null;
        if (this.actionAdd) {
            trailingAction = this.createAddButton();
        } else if (this.actionRemove) {
            trailingAction = this.createRemoveButton();
        } else {
            trailingAction = keepAllowedElements(
                this.getChildrenForSlot('trailing'),
                UIField.allowedTrailingElementTags
            )[0];
            if (trailingAction) {
                trailingAction.classList.add('ui-field__trailing-action');
            }
        }
        return trailingAction;
    }

    /**
     * Gets the number of controls in the field.
     * @param {boolean} [includeFieldsetFriendly] Include fieldset-friendly HTML elements (radios or checkboxes)
     * @returns {number}
     */
    getControlsLength(includeFieldsetFriendly = false) {
        const selectors = (
            includeFieldsetFriendly
                ? BaseNativeFormElements
                : NativeFormElements
        )
            .concat(CustomFormElements)
            .join(',');
        return this.querySelectorAll(selectors).length;
    }

    /**
     * Sets content to label element inside ui-field.
     * @param {string} content
     * @returns {UIField}
     */
    setLabelContent(content) {
        const control = this.getControl(true);
        const isFieldsetFriendly = this.isFieldsetFriendlyControl();

        let label = this.querySelector('.ui-field__label-inner');
        if (!label) {
            if (!content) {
                return this;
            }
            label = this.createElement({
                tagName: isFieldsetFriendly ? 'span' : 'label',
                attributes: {},
                classList: { 'ui-field__label-inner': true },
            });
            const controlId = this.getControlId();
            if (controlId) {
                label.setAttribute('for', controlId);
            }
            const container = this.labelContainer;
            if (container) {
                if (container.childNodes.length > 0) {
                    container.insertBefore(label, container.firstChild);
                } else {
                    container.appendChild(label);
                }
            }
        }
        label.innerHTML = content;

        // Add click handler for custom form component labels
        if (control && CustomFormElements.includes(control.localName)) {
            if (label && label.parentNode) {
                if (this._labelClickListener) {
                    this._labelClickListener.label.removeEventListener(
                        'click',
                        this._labelClickListener.handler
                    );
                    this._labelClickListener = null;
                }

                const newLabel = label.cloneNode(true);
                label.parentNode.replaceChild(newLabel, label);
                label = newLabel;
            }

            // Store listener for cleanup on disconnect
            const clickHandler = async (e) => {
                e.preventDefault();
                await handleLabelClick(control);
            };
            label.addEventListener('click', clickHandler);
            this._labelClickListener = { label, handler: clickHandler };
        }

        return this;
    }

    /**
     * Links labels to controls.
     * @returns {UIField}
     */
    linkLabels() {
        const label = this.querySelector('.ui-field__label-inner');
        if (!label) {
            return this;
        }
        const controlId = this.getControlId();
        if (controlId) {
            label.setAttribute('for', controlId);
            const currencyLabel = this.querySelector('label.units');
            if (currencyLabel) {
                currencyLabel.setAttribute('for', controlId);
            }
        }
        this.updateAriaLabels();
        return this;
    }

    /**
     * Moves the trailing action to the appropriate place based on the screen size.
     */
    moveTrailingAction() {
        if (!this.trailingAction || this.actionAdd || this.actionRemove) {
            return;
        }
        const isMobile = browser.mobileMediaMatches();
        const isFluid = this.layout === UIField.Layout.Fluid;
        if (!isFluid && !isMobile) {
            // If the layout is not fluid and not mobile, append the trailing action to the inputgroup
            this.inputGroup.appendChild(this.trailingAction);
        } else {
            this.control.appendChild(this.trailingAction);
        }
    }

    /**
     * Checks if the HTML control passed through the default slot is a radio or checkbox
     * @returns {boolean}
     */
    isFieldsetFriendlyControl() {
        const control = this.getControl(true);
        const controlsLength = this.getControlsLength(true);
        return (
            control &&
            ['radio', 'checkbox'].includes(control.type) &&
            controlsLength > 1
        );
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name, oldValue, newValue) {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }

        switch (name) {
            case 'required':
            case 'label':
                this.updateLabel();
                break;
            case 'action-add':
            case 'action-remove':
                this.updateTrailingAction();
                break;
            case 'units':
                this.updateUnits();
                break;
        }
    }

    /**
     * Extracts units from the markup if present (legacy approach).
     */
    getUnitsFromMarkup() {
        const unitsElement = this.querySelector('.units');
        if (unitsElement && !this.classList.contains('-text')) {
            // Check if previous visible sibling matches native input/select controls
            // Supports legacy usage where units are placed after other elements
            const nativeControlsSelector =
                'input:not([type="checkbox"]):not([type="radio"]), select';
            let prev = unitsElement.previousElementSibling;
            while (prev && !this.isVisibleControl(prev)) {
                prev = prev.previousElementSibling;
            }
            if (prev && prev.matches(nativeControlsSelector)) {
                this.units = unitsElement.textContent;
                unitsElement.remove();
            }
        }
    }

    /**
     * Update or build the units label inside the field if only a native input or select is present.
     */
    updateUnits() {
        const nativeControlsSelector =
            ':scope > input:not([type="checkbox"]):not([type="radio"]), :scope > select';

        // Remove wrapper if units are not present, and move control to the original place
        if (!this.units) {
            const control = this._unitsWrapper?.querySelector(
                nativeControlsSelector
            );
            if (control) {
                control.removeAttribute('aria-describedby');
            }
            if (this._unitsWrapper?.parentNode) {
                this._unitsWrapper.parentNode.insertBefore(
                    control,
                    this._unitsWrapper
                );
                this._unitsWrapper.parentNode.removeChild(this._unitsWrapper);
                this._unitsWrapper = null;
            }
            return;
        }

        // If wrapper exists, just update units text
        if (this._unitsWrapper && this.unitsElement) {
            this.unitsElement.textContent = this.units;
            return;
        }

        // Create wrapper and units element
        const controls = this.inputGroup
            ? this.inputGroup.querySelectorAll(nativeControlsSelector)
            : this.control.querySelectorAll(nativeControlsSelector);
        let lastControl = null;
        for (const control of Array.from(controls).reverse()) {
            if (this.isVisibleControl(control)) {
                lastControl = control;
                break;
            }
        }

        if (!lastControl) {
            return;
        }

        const wrapper = document.createElement('span');
        wrapper.className = 'ui-field__units-wrapper';
        lastControl.parentNode.insertBefore(wrapper, lastControl);
        wrapper.appendChild(lastControl);

        const unitsEl = document.createElement('span');
        unitsEl.className = 'ui-field__units';
        unitsEl.textContent = this.units;
        unitsEl.id = 'ui-field__units-' + Digest.randomId();
        wrapper.appendChild(unitsEl);
        lastControl.setAttribute('aria-describedby', unitsEl.id);
        this._unitsWrapper = wrapper;
    }

    /**
     * @inheritDoc
     */
    render() {
        /* If there is already <ui-inputgroup>, put class accordingly used in ui-field for that */
        const inputGroupElement = this.querySelector('ui-inputgroup');
        if (inputGroupElement) {
            inputGroupElement.classList.add('ui-field__inputgroup');
        }

        const isFieldsetFriendly = this.isFieldsetFriendlyControl();

        /**
         * @type {ElementConfig}
         */
        const legendElementConfig = {
            tagName: 'legend',
            classList: {
                '-visually-hidden': 'true', // Hide legend if label is provided - Safari still can't style legend properly so we are adding one more visible div with aria-hidden
            },
            children: [this.label],
        };

        /**
         * @type {ElementConfig}
         */
        const labelElementConfig = {
            tagName: 'div',
            classList: {
                'ui-field__label': true,
            },
            attributes: {
                'aria-hidden': isFieldsetFriendly && this.label ? 'true' : null,
            },
            children: [
                !!this.label && {
                    tagName: isFieldsetFriendly ? 'span' : 'label',
                    classList: {
                        'ui-field__label-inner': true,
                    },
                    children: [
                        this.label,
                        this.required && {
                            tagName: 'span',
                            classList: {
                                'ui-field__required-marker': true,
                            },
                            attributes: {
                                'aria-hidden': 'true',
                            },
                            children: [` (${UIField.labels.required})`],
                        },
                    ],
                },
            ].concat(this.getChildrenForSlot('label')),
        };

        const trailingAction = this.getTrailingAction();

        /**
         * @type {ElementConfig}
         */
        const controlElementConfig = {
            tagName: 'div',
            classList: {
                'ui-field__control': true,
            },
            children: [].filter.call(this.childNodes, (node) => {
                return !node.tagName || !node.hasAttribute('slot');
            }),
        };

        /**
         * @type {ElementConfig}
         */
        const wrapperElementConfig = {
            tagName: isFieldsetFriendly && this.label ? 'fieldset' : 'div',
            classList: {
                'ui-field__wrapper': true,
            },
            children: [
                isFieldsetFriendly && this.label && legendElementConfig,
                labelElementConfig,
                controlElementConfig,
            ],
        };

        this.updateElement({
            children: [wrapperElementConfig],
        });

        this.applyTrailingActionToControl(trailingAction);
        this.getUnitsFromMarkup();
        this.updateUnits();
        this.linkLabels();
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.checkErrors();
        this.updateLabel();
        this.updateAriaLabels();
        this.resizeHandler = debounce(this.moveTrailingAction.bind(this), 200);
        window.addEventListener('resize', this.resizeHandler);
        this.moveTrailingAction();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        window.removeEventListener('resize', this.resizeHandler);
        if (this._labelClickListener) {
            this._labelClickListener.label.removeEventListener(
                'click',
                this._labelClickListener.handler
            );
            this._labelClickListener = null;
        }
    }
}

UIField.defineElement('ui-field', styles);
export { UIField };
